import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { PushService } from './services/push.service';
import { UpdateService } from './services/update.service';
import { PwaBannerComponent } from './components/shared/pwa-banner/pwa-banner.component';
import { PushPromptBannerComponent } from './components/shared/push-prompt-banner/push-prompt-banner.component';
import { UpdateBannerComponent } from './components/shared/update-banner/update-banner.component';
import { ConfirmDialogComponent } from './components/shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, PwaBannerComponent, PushPromptBannerComponent, UpdateBannerComponent, ConfirmDialogComponent],
  template: `
    <router-outlet />
    <!-- One app-wide confirm/notice dialog, driven by ConfirmService. Mounted
         here so it renders above every page and modal in the app. -->
    <app-confirm-dialog />
    <!-- All bottom banners live in ONE fixed stack so two banners can never
         paint over each other (they used to share bottom:0/z-999 and
         overlap). While any banner is visible, styles.css reserves
         padding-bottom on the shell scrollers via body:has(...) so the
         banner never covers the last rows of a list. -->
    <div class="bottom-banner-stack">
      <!-- Not on a station display board: nobody stands at that screen to
           install anything, and it never subscribes to push (see ngOnInit),
           so "Notifications are blocked" would sit over the board forever. -->
      @if (!isDisplay) {
        <app-pwa-banner />
        <app-push-prompt-banner />
      }
      <app-update-banner />
    </div>
  `
})
export class AppComponent implements OnInit {
  /** Signed in as a station display board (members.is_display). */
  isDisplay = false;

  constructor(
    private auth: AuthService,
    private pushService: PushService,
    private updateService: UpdateService
  ) {}

  ngOnInit(): void {
    // Re-evaluate push subscription on every auth change, not just at app
    // boot. Without this, a user who opens the PWA while logged out (or
    // with an expired session) never subscribes to push for the rest of
    // the session — ngOnInit on the root component only runs once. Also
    // covers the shared-device case: when user B logs in after user A
    // logs out on the same phone, we reset PushService's "subscribed"
    // flag so the next subscribeToPush() actually runs and re-points the
    // existing browser endpoint at user B via /push/subscribe upsert.
    let lastUserId: number | null = null;
    this.auth.currentUser$.subscribe((user) => {
      this.isDisplay = !!user?.is_display;
      const newId = user?.id ?? null;
      if (newId === lastUserId) return;

      // If we had a previous user, the prior session's subscribed-flag is
      // stale — clear it so the next subscribeToPush() actually fires.
      if (lastUserId !== null) {
        this.pushService.reset();
      }
      lastUserId = newId;

      // A station display board raises its own alarm on screen and through
      // the OS, and the API blocks /push/subscribe for its session anyway —
      // asking the station PC for notification permission here would only
      // produce a permission prompt nobody is standing there to answer.
      if (newId !== null && !user?.is_display) {
        this.pushService.subscribeToPush();
      }
    });

    // Detect new app versions and prompt the user to reload (see
    // UpdateService + UpdateBannerComponent). Replaces the old silent
    // force-reload, which only checked once at boot — so a long-open
    // dispatcher session never picked up releases and some users had to
    // reinstall the PWA to update.
    this.updateService.init();
  }
}
